// fallow-ignore-file code-duplication -- page-owned Dryad attribute pickers intentionally share Pluma Select wiring
import { useMemo, useState } from 'react';
import { Select } from '@customerio/pluma-components/react';
import {
  OBJECT_ATTRIBUTE_TYPE,
  OBJECT_FILTER_OPERATOR_FAMILIES,
  AttributeRow as SharedAttributeRow,
  groupsForFamilies,
  objectFilterBuilder,
} from '@/features/dryad';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { useAttributeCatalog, useObjectAttributeValues } from '@/services/db/attribute/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { isEmptyString } from '@/utils/string';

type ObjectAttributeRowProps = { id: string };
type AttributeOption = { value: string; label: string };

export function ObjectAttributeRow({ id }: ObjectAttributeRowProps) {
  const workspaceId = requireWorkspaceId();
  const meta = objectFilterBuilder.useMeta();
  const { state } = objectFilterBuilder.useLeafState(id, OBJECT_ATTRIBUTE_TYPE);
  const [fieldTouched, setFieldTouched] = useState(() => state.field !== '');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm] = useDebouncedValue(searchTerm, 300);
  const objectTypeId = String(state.typeId);
  const attributes = useAttributeCatalog(workspaceId, {
    objectTypeId,
    search: isEmptyString(debouncedTerm) ? undefined : debouncedTerm,
  });
  const values = useObjectAttributeValues(workspaceId, objectTypeId, state.field);
  const attributeOptions = useMemo<AttributeOption[]>(() => {
    const needle = searchTerm.trim().toLowerCase();
    const names = new Set(
      [...meta.attributeNames, ...(attributes.data?.pages ?? []).flat()].filter((name) =>
        name.toLowerCase().includes(needle),
      ),
    );
    const custom = !isEmptyString(searchTerm) && !names.has(searchTerm) ? [searchTerm] : [];
    if (!isEmptyString(state.field)) names.add(state.field);
    return [...custom, ...names].map((name) => ({ value: name, label: name }));
  }, [attributes.data?.pages, meta.attributeNames, searchTerm, state.field]);
  const valueOptions = useMemo(() => (values.data ?? []).map((value) => ({ value, label: value })), [values.data]);

  return (
    <SharedAttributeRow
      id={id}
      conditionType={OBJECT_ATTRIBUTE_TYPE}
      operatorGroups={(field) => groupsForFamilies(OBJECT_FILTER_OPERATOR_FAMILIES, field)}
      isDisabled={meta.isDisabled}
      valueOptions={valueOptions}
      isLoadingValueOptions={values.isFetching}
      isFieldTouched={fieldTouched}
      onFieldTouched={() => setFieldTouched(true)}
      renderNamePicker={({ field, error, isDisabled, onFieldChange, onTouched }) => (
        <Select
          data-testid="dryad-object-attribute-name"
          ariaLabel="Object attribute"
          size="sm"
          placeholder="Select an attribute"
          options={attributeOptions}
          value={isEmptyString(field) ? undefined : field}
          error={error}
          isDisabled={isDisabled}
          isSearchable
          searchPlaceholder="Search attributes"
          searchInputValue={searchTerm}
          onSearchInput={(event) => setSearchTerm(event.target.value)}
          isLoading={attributes.isFetching}
          onOpenChange={(isOpen: boolean) => {
            if (!isOpen) {
              onTouched();
              setSearchTerm('');
            }
          }}
          onChange={(value: string | null) => {
            setSearchTerm('');
            if (value != null) onFieldChange(value);
          }}
        />
      )}
    />
  );
}
